import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { fetchOutstandingSurveyForTimer, submitSurveyForTimer } from '../services/surveyService';
import {
  extractApiMessage,
  extractSurveyValidationDetails,
  isDuplicateSurveySubmissionError,
  normalizeSurveyRequirement,
} from '../utils/surveyGate';

const SurveyGateContext = createContext(null);
const SURVEY_ANSWER_MAX_LENGTH = 500;

const buildInitialAnswers = (questions = []) =>
  questions.reduce((accumulator, question) => {
    accumulator[question.id] = '';
    return accumulator;
  }, {});

const getAnswerValidationError = (value, isRequired = false) => {
  const normalizedValue = `${value || ''}`;

  if (isRequired && !normalizedValue.trim()) {
    return 'This field is required.';
  }

  if (normalizedValue.length > SURVEY_ANSWER_MAX_LENGTH) {
    return `Answer must be ${SURVEY_ANSWER_MAX_LENGTH} characters or less.`;
  }

  return '';
};

export const SurveyGateProvider = ({ children }) => {
  const resolverRef = useRef(null);
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    survey: null,
    answers: {},
    fieldErrors: {},
    formError: '',
    isSubmitting: false,
  });

  const resolveAndReset = useCallback((didCompleteSurvey) => {
    if (resolverRef.current) {
      resolverRef.current(didCompleteSurvey);
      resolverRef.current = null;
    }

    setDialogState({
      isOpen: false,
      survey: null,
      answers: {},
      fieldErrors: {},
      formError: '',
      isSubmitting: false,
    });
  }, []);

  const setSurveyState = useCallback((survey) => {
    setDialogState({
      isOpen: true,
      survey,
      answers: buildInitialAnswers(survey?.questions),
      fieldErrors: {},
      formError: '',
      isSubmitting: false,
    });
  }, []);

  const requestSurveyCompletion = useCallback(
    (surveyPayload) => {
      const survey = normalizeSurveyRequirement(surveyPayload);

      if (!survey) {
        return Promise.resolve(true);
      }

      if (resolverRef.current) {
        resolverRef.current(false);
      }

      return new Promise((resolve) => {
        resolverRef.current = resolve;
        setSurveyState(survey);
      });
    },
    [setSurveyState]
  );

  const handleClose = useCallback(() => {
    if (dialogState.isSubmitting) {
      return;
    }

    resolveAndReset(false);
  }, [dialogState.isSubmitting, resolveAndReset]);

  const handleDialogClose = useCallback(
    (_, reason) => {
      if (reason === 'backdropClick') {
        return;
      }

      handleClose();
    },
    [handleClose]
  );

  const handleAnswerChange = useCallback((question, value) => {
    const validationMessage = getAnswerValidationError(value, question?.isRequired);

    setDialogState((currentState) => ({
      ...currentState,
      answers: {
        ...currentState.answers,
        [question.id]: value,
      },
      fieldErrors: {
        ...currentState.fieldErrors,
        [question.id]: validationMessage,
      },
      formError: '',
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const currentSurvey = dialogState.survey;

    if (!currentSurvey) {
      return;
    }

    const nextFieldErrors = {};

    currentSurvey.questions.forEach((question) => {
      const answerValue = dialogState.answers[question.id];
      const validationMessage = getAnswerValidationError(answerValue, question.isRequired);

      if (validationMessage) {
        nextFieldErrors[question.id] = validationMessage;
      }
    });

    if (Object.keys(nextFieldErrors).length > 0) {
      setDialogState((currentState) => ({
        ...currentState,
        fieldErrors: nextFieldErrors,
        formError: 'Please answer all required questions.',
      }));
      return;
    }

    setDialogState((currentState) => ({
      ...currentState,
      isSubmitting: true,
      formError: '',
    }));

    const payload = {
      periodKey: currentSurvey.periodKey,
      answers: currentSurvey.questions.map((question) => ({
        questionId: question.id,
        answerText: `${dialogState.answers[question.id] || ''}`.trim(),
      })),
    };

    try {
      await submitSurveyForTimer(payload);
      resolveAndReset(true);
    } catch (error) {
      if (isDuplicateSurveySubmissionError(error)) {
        try {
          const outstandingResponse = await fetchOutstandingSurveyForTimer();
          const outstandingSurvey = normalizeSurveyRequirement(outstandingResponse?.data);

          if (outstandingSurvey) {
            setSurveyState(outstandingSurvey);
          } else {
            resolveAndReset(true);
          }
          return;
        } catch (refreshError) {
          setDialogState((currentState) => ({
            ...currentState,
            isSubmitting: false,
            formError: extractApiMessage(refreshError),
          }));
          return;
        }
      }

      const validationDetails = extractSurveyValidationDetails(error, currentSurvey.questions);

      setDialogState((currentState) => ({
        ...currentState,
        isSubmitting: false,
        fieldErrors: Object.keys(validationDetails.fieldErrors).length
          ? validationDetails.fieldErrors
          : currentState.fieldErrors,
        formError: validationDetails.formError || extractApiMessage(error),
      }));
    }
  }, [dialogState.answers, dialogState.survey, resolveAndReset, setSurveyState]);

  const contextValue = useMemo(
    () => ({
      requestSurveyCompletion,
    }),
    [requestSurveyCompletion]
  );

  const formattedSurveyDate = dialogState.survey?.surveyDate
    ? dayjs(dialogState.survey.surveyDate).format('MMMM D, YYYY')
    : '';

  return (
    <SurveyGateContext.Provider value={contextValue}>
      {children}
      <Dialog
        open={dialogState.isOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          Weekly Survey
          <IconButton
            aria-label="close survey"
            onClick={handleClose}
            disabled={dialogState.isSubmitting}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowX: 'hidden' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body1">
                Before we start the timer, please take a moment to complete the weekly survey.
              </Typography>
              {formattedSurveyDate ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Survey date: {formattedSurveyDate}
                </Typography>
              ) : null}
            </Box>

            {dialogState.formError ? <Alert severity="error">{dialogState.formError}</Alert> : null}

            {dialogState.survey?.questions?.map((question) => {
              const answerValue = dialogState.answers[question.id] || '';
              const helperMessage =
                dialogState.fieldErrors[question.id] || `${answerValue.length}/${SURVEY_ANSWER_MAX_LENGTH} characters`;

              return (
                <TextField
                  key={question.id}
                  label={question.questionText}
                  value={answerValue}
                  onChange={(event) => handleAnswerChange(question, event.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  inputProps={{ maxLength: SURVEY_ANSWER_MAX_LENGTH }}
                  error={Boolean(dialogState.fieldErrors[question.id])}
                  helperText={helperMessage}
                  sx={{
                    '& .MuiInputBase-inputMultiline': {
                      overflowY: 'auto',
                      resize: 'none',
                    },
                  }}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={dialogState.isSubmitting} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={dialogState.isSubmitting}>
            {dialogState.isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Submit and Start Timer'}
          </Button>
        </DialogActions>
      </Dialog>
    </SurveyGateContext.Provider>
  );
};

SurveyGateProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useSurveyGate = () => {
  const context = useContext(SurveyGateContext);

  if (!context) {
    throw new Error('useSurveyGate must be used within a SurveyGateProvider');
  }

  return context;
};
